/* eslint-disable no-param-reassign */
/**
 =========================================================
 * Material Kit 2 React - v2.1.0
 =========================================================
 * Product Page: https://www.creative-tim.com/product/material-kit-react
 * Copyright 2023 Creative Tim
 =========================================================
 * This notice and the permission notice shall be included
 * in all copies or substantial portions of the Software.
 */

import React, { Fragment, useEffect, useState, useMemo } from "react";

// react-router
import { Link } from "react-router-dom";

// prop-types
import PropTypes from "prop-types";

// @mui material
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import MuiLink from "@mui/material/Link";

// Material Kit 2 components
import MKBox from "components/box";
import MKTypography from "components/typography";

// example components
import DefaultNavbarDropdown from "examples/Navbars/DefaultNavbar/DefaultNavbarDropdown";
import DefaultNavbarMobile from "examples/Navbars/DefaultNavbar/DefaultNavbarMobile";

// theme helpers
import breakpoints from "assets/theme/base/breakpoints";
import websiteNameByBrand from "utils/websiteNameByBrand";

/* -------------------------------------------------------------------------- */
/*                                COMPONENT                                   */
/* -------------------------------------------------------------------------- */
function DefaultNavbar({
                         brand: brandProp,
                         routes,
                         transparent,
                         light,
                         sticky,
                         relative,
                         center,
                         profileImage,
                       }) {
  /* ---------------------------- derive brand data ------------------------- */
  // If the parent already passed a brand node, use it.
  // Otherwise compute brand data the first time we run in the browser.
  const brandData = useMemo(() => (!brandProp ? websiteNameByBrand() : null), [brandProp]);

  // Guard: until we have either a prop or computed data, render nothing
  if (!brandProp && !brandData) return null;

  // Compose the element we’ll actually render
  const brandNode =
    brandProp ||
    (brandData && (
      <img
        src={brandData.logo}
        alt={brandData.title}
        style={
          brandData.title === "IDMAPS"
            ? { width: "10rem", height: "5rem" }
            : { width: "5rem", height: "5rem" }
        }
      />
    ));

  /* --------------------------- local component state ---------------------- */
  const [dropdown, setDropdown]               = useState(null);
  const [dropdownEl, setDropdownEl]           = useState(null);
  const [dropdownName, setDropdownName]       = useState("");
  const [nestedDropdown, setNestedDropdown]   = useState(null);
  const [nestedDropdownEl, setNestedDropdownEl] = useState(null);
  const [nestedDropdownName, setNestedDropdownName] = useState("");
  const [arrowRef, setArrowRef]               = useState(null);
  const [mobileNavbar, setMobileNavbar]       = useState(false);
  const [mobileView, setMobileView]           = useState(false);

  const toggleMobileNavbar = () => setMobileNavbar((prev) => !prev);

  /* ------------------------------ resize hook ----------------------------- */
  useEffect(() => {
    const displayMobileNavbar = () => {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
        setMobileNavbar(false);
      } else {
        setMobileView(false);
        setMobileNavbar(false);
      }
    };
    window.addEventListener("resize", displayMobileNavbar);
    displayMobileNavbar();                 // initial check
    return () => window.removeEventListener("resize", displayMobileNavbar);
  }, []);

  /* ------------------------- top-level nav items -------------------------- */
  const renderNavbarItems = routes.map(
    ({ name, icon, href, route, collapse }) => (
      <DefaultNavbarDropdown
        key={name}
        name={name}
        icon={icon}
        href={href}
        route={route}
        collapse={Boolean(collapse)}
        light={light}
        onMouseEnter={({ currentTarget }) => {
          if (collapse) {
            setDropdown(currentTarget);
            setDropdownEl(currentTarget);
            setDropdownName(name);
          }
        }}
        onMouseLeave={() => collapse && setDropdown(null)}
      />
    )
  );

  /* ----------------------------- dropdown menu --------------------------- */
  const renderRoutes = routes.map(
    ({ name, collapse, columns, rowsPerColumn }) => {
      if (!collapse || name !== dropdownName) return null;

      // 1️⃣ columns layout
      if (columns) {
        const calculateColumns = collapse.reduce((acc, item, idx) => {
          const chunk = Math.floor(idx / rowsPerColumn);
          acc[chunk] = acc[chunk] || [];
          acc[chunk].push(item);
          return acc;
        }, []);

        return (
          <Grid key={name} container spacing={3} py={1} px={1.5}>
            {calculateColumns.map((colArr, colIdx) => (
              <Grid
                key={`grid-${colIdx}`}
                item
                xs={12 / columns}
                sx={{ position: "relative" }}
              >
                {colArr.map((col, idx) => (
                  <Fragment key={col.name}>
                    <MKTypography
                      display="block"
                      variant="button"
                      fontWeight="bold"
                      textTransform="capitalize"
                      py={1}
                      px={0.5}
                      mt={idx !== 0 ? 2 : 0}
                    >
                      {col.name}
                    </MKTypography>

                    {col.collapse.map((item) => (
                      <MKTypography
                        key={item.name}
                        component={item.route ? Link : MuiLink}
                        {...(item.route
                            ? { to: item.route }
                            : {
                                 href: item.href ?? "#",
                                 target: item.href ? "_blank" : undefined,
                                 rel: item.href ? "noreferrer" : undefined,
                                 onClick: !item.href ? (e) => e.preventDefault() : undefined,
                          })}
                        minWidth="11.25rem"
                        display="block"
                        variant="button"
                        color="text"
                        textTransform="capitalize"
                        fontWeight="regular"
                        py={0.625}
                        px={2}
                        sx={({ palette: { grey, dark }, borders: { borderRadius } }) => ({
                          borderRadius: borderRadius.md,
                          cursor: "pointer",
                          transition: "all 300ms linear",
                          "&:hover": {
                            backgroundColor: grey[200],
                            color: dark.main,
                          },
                        })}
                      >
                        {item.name}
                      </MKTypography>
                    ))}
                  </Fragment>
                ))}

                {colIdx !== 0 && (
                  <Divider
                    orientation="vertical"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "-4px",
                      transform: "translateY(-45%)",
                      height: "90%",
                    }}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        );
      }

      // 2️⃣ simple list layout
      return collapse.map((item) => {
        const linkProps = item.route
          ? { component: Link, to: item.route }
          : { component: MuiLink, href: item.href, target: "_blank", rel: "noreferrer" };

        return (
          <MKTypography
            key={item.name}
            {...linkProps}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            variant="button"
            textTransform="capitalize"
            minWidth={item.description ? "14rem" : "12rem"}
            color={item.description ? "dark" : "text"}
            fontWeight={item.description ? "bold" : "regular"}
            py={item.description ? 1 : 0.625}
            px={2}
            sx={({ palette: { grey, dark }, borders: { borderRadius } }) => ({
              borderRadius: borderRadius.md,
              cursor: "pointer",
              transition: "all 300ms linear",
              "&:hover": {
                backgroundColor: grey[200],
                color: dark.main,
                "& *": { color: dark.main },
              },
            })}
            onMouseEnter={({ currentTarget }) => {
              if (item.dropdown) {
                setNestedDropdown(currentTarget);
                setNestedDropdownEl(currentTarget);
                setNestedDropdownName(item.name);
              }
            }}
            onMouseLeave={() => item.dropdown && setNestedDropdown(null)}
          >
            {item.description ? (
              <MKBox>
                {item.name}
                <MKTypography
                  display="block"
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ transition: "all 300ms linear" }}
                >
                  {item.description}
                </MKTypography>
              </MKBox>
            ) : (
              item.name
            )}
            {item.collapse && (
              <Icon
                fontSize="small"
                sx={{ fontWeight: "normal", verticalAlign: "middle", mr: -0.5 }}
              >
                keyboard_arrow_right
              </Icon>
            )}
          </MKTypography>
        );
      });
    }
  );

  const dropdownMenu = (
    <Popper
      anchorEl={dropdown}
      open={Boolean(dropdown)}
      placement="top-start"
      transition
      style={{ zIndex: 10 }}
      modifiers={[
        {
          name: "arrow",
          enabled: true,
          options: { element: arrowRef },
        },
      ]}
      onMouseEnter={() => setDropdown(dropdownEl)}
      onMouseLeave={() => {
        if (!nestedDropdown) {
          setDropdown(null);
          setDropdownName("");
        }
      }}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          sx={{
            transformOrigin: "left top",
            background: ({ palette: { white } }) => white.main,
          }}
        >
          <MKBox borderRadius="lg">
            <MKTypography variant="h1" color="white">
              <Icon ref={setArrowRef} sx={{ mt: -3 }}>
                arrow_drop_up
              </Icon>
            </MKTypography>
            <MKBox shadow="lg" borderRadius="lg" p={2} mt={2}>
              {renderRoutes}
            </MKBox>
          </MKBox>
        </Grow>
      )}
    </Popper>
  );

  /* --------------------------- nested dropdown --------------------------- */
  const renderNestedRoutes = routes.flatMap(({ collapse, columns }) =>
    collapse && !columns
      ? collapse.flatMap(({ name: parentName, collapse: nestedCollapse }) =>
        parentName === nestedDropdownName
          ? nestedCollapse.map((item) => {
            const linkProps = item.route
              ? { component: Link, to: item.route }
              : {
                component: MuiLink,
                href: item.href,
                target: "_blank",
                rel: "noreferrer",
              };

            return (
              <MKTypography
                key={item.name}
                {...linkProps}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                variant="button"
                textTransform="capitalize"
                minWidth={item.description ? "14rem" : "12rem"}
                color={item.description ? "dark" : "text"}
                fontWeight={item.description ? "bold" : "regular"}
                py={item.description ? 1 : 0.625}
                px={2}
                sx={({ palette: { grey, dark }, borders: { borderRadius } }) => ({
                  borderRadius: borderRadius.md,
                  cursor: "pointer",
                  transition: "all 300ms linear",
                  "&:hover": {
                    backgroundColor: grey[200],
                    color: dark.main,
                    "& *": { color: dark.main },
                  },
                })}
              >
                {item.description ? (
                  <MKBox>
                    {item.name}
                    <MKTypography
                      display="block"
                      variant="button"
                      color="text"
                      fontWeight="regular"
                      sx={{ transition: "all 300ms linear" }}
                    >
                      {item.description}
                    </MKTypography>
                  </MKBox>
                ) : (
                  item.name
                )}
                {item.collapse && (
                  <Icon
                    fontSize="small"
                    sx={{ fontWeight: "normal", verticalAlign: "middle", mr: -0.5 }}
                  >
                    keyboard_arrow_right
                  </Icon>
                )}
              </MKTypography>
            );
          })
          : []
      )
      : []
  );

  const nestedDropdownMenu = (
    <Popper
      anchorEl={nestedDropdown}
      open={Boolean(nestedDropdown)}
      placement="right-start"
      transition
      style={{ zIndex: 10 }}
      onMouseEnter={() => setNestedDropdown(nestedDropdownEl)}
      onMouseLeave={() => {
        setNestedDropdown(null);
        setNestedDropdownName("");
        setDropdown(null);
      }}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          sx={{
            transformOrigin: "left top",
            background: ({ palette: { white } }) => white.main,
          }}
        >
          <MKBox ml={2.5} mt={-2.5} borderRadius="lg">
            <MKBox shadow="lg" borderRadius="lg" py={1.5} px={1} mt={2}>
              {renderNestedRoutes}
            </MKBox>
          </MKBox>
        </Grow>
      )}
    </Popper>
  );

  /* ------------------------------ rendering ------------------------------ */
  return (
    <Container sx={sticky ? { position: "sticky", top: 0, zIndex: 10 } : undefined}>
      <MKBox
        py={1}
        px={{ xs: 4, sm: transparent ? 2 : 3, lg: transparent ? 0 : 2 }}
        my={relative ? 0 : 2}
        mx={relative ? 0 : 3}
        width={relative ? "100%" : "calc(100% - 48px)"}
        borderRadius="xl"
        shadow={transparent ? "none" : "md"}
        color={light ? "white" : "dark"}
        position={relative ? "relative" : "absolute"}
        left={0}
        zIndex={3}
        sx={({ palette: { transparent: t, white }, functions: { rgba } }) => ({
          backgroundColor: transparent ? t.main : rgba(white.main, 0.8),
          backdropFilter: transparent ? "none" : "saturate(200%) blur(30px)",
        })}
      >
        <MKBox display="flex" justifyContent="space-between" alignItems="center">
          {/* brand / logo */}
          <MKBox
            component={Link}
            to="/"
            lineHeight={1}
            py={transparent ? 1.5 : 0.75}
            pl={relative || transparent ? 0 : { xs: 0, lg: 1 }}
          >
            {brandNode}
          </MKBox>

          {/* desktop menu */}
          <MKBox
            color="inherit"
            display={{ xs: "none", lg: "flex" }}
            ml="auto"
            mr={center ? "auto" : 0}
          >
            {renderNavbarItems}
          </MKBox>

          {/* burger icon */}
          <MKBox
            display={{ xs: "inline-block", lg: "none" }}
            lineHeight={0}
            py={1.5}
            pl={1.5}
            color={transparent ? "white" : "inherit"}
            sx={{ cursor: "pointer" }}
            onClick={toggleMobileNavbar}
          >
            <Icon fontSize="default">{mobileNavbar ? "close" : "menu"}</Icon>
          </MKBox>

          {/* optional profile picture / initials */}
          {profileImage && (
            <MKTypography variant="button" fontWeight="bold" color={light ? "white" : "dark"}>
              {profileImage}
            </MKTypography>
          )}
        </MKBox>

        {/* mobile drawer */}
        <MKBox
          bgColor={transparent ? "white" : "transparent"}
          shadow={transparent ? "lg" : "none"}
          borderRadius="xl"
          px={transparent ? 2 : 0}
        >
          {mobileView && (
            <DefaultNavbarMobile
              routes={routes.filter((r) => r.type === "nav")}
              open={mobileNavbar}
            />
          )}
        </MKBox>
      </MKBox>

      {/* floating dropdowns */}
      {dropdownMenu}
      {nestedDropdownMenu}
    </Container>
  );
}

/* -------------------------------------------------------------------------- */
/*                                PROP TYPES                                  */
/* -------------------------------------------------------------------------- */
DefaultNavbar.propTypes = {
  brand: PropTypes.node,                   // optional override
  routes: PropTypes.arrayOf(PropTypes.shape).isRequired,
  transparent: PropTypes.bool,
  light: PropTypes.bool,
  sticky: PropTypes.bool,
  relative: PropTypes.bool,
  center: PropTypes.bool,
  profileImage: PropTypes.node,
};

DefaultNavbar.defaultProps = {
  brand: null,
  transparent: false,
  light: false,
  sticky: false,
  relative: false,
  center: false,
  profileImage: null,
};

export default DefaultNavbar;
